import {
  CarouselLoadingState,
  RenderTileArgs,
  Tile,
  TileLoadingState,
  TileTitle,
  WebGLCarouselRenderer,
  WebGLContainer,
} from "@yv/sonic-ui-library";
import { TMDBMovie, TMDBTVShow, useTMDBQuerySimple } from "../../hooks/tmdb";
import { transformTMDBItem } from "./utils";
import { memo, useCallback } from "react";

interface CarouselDataWrapperProps {
  urlTemplate: string;
  carouselIndex: number;
  isCarouselFocused: boolean;
  viewportWidth: number;
  carouselItemHeight: number;
  tileWidth: number;
  tileHeight: number;
  padding: number;
  gap: number;
  position: { x: number; y: number };
  carouselTitle?: string;
}

export const CarouselDataWrapper = memo(function CarouselDataWrapper({
  urlTemplate,
  carouselIndex,
  carouselTitle,
  isCarouselFocused,
  viewportWidth,
  carouselItemHeight,
  tileWidth,
  tileHeight,
  padding,
  gap,
  position,
}: CarouselDataWrapperProps) {
  const { data: carouselData } = useTMDBQuerySimple<TMDBMovie | TMDBTVShow>({
    template: urlTemplate,
  });

  const renderTile = useCallback(
    function renderTile({
      index: tileIndex,
      isFocused: isTileFocused,
      x,
      y,
    }: RenderTileArgs) {
      const entity = carouselData?.results[tileIndex];
      if (!entity) {
        return null;
      }
      const item = transformTMDBItem(carouselData.results[tileIndex]);
      const tileLoadingState = TileLoadingState.LOADING;

      return (
        <Tile
          key={`tile-${carouselIndex}-${tileIndex}`}
          index={tileIndex}
          isFocused={isCarouselFocused && isTileFocused}
          imageUrl={item.image}
          title={item.title}
          width={tileWidth}
          height={tileHeight}
          x={x}
          y={y}
          tileLoadingState={tileLoadingState}
          // filters={filters}
          // skipImageLoad
        />
      );
    },
    [carouselData, carouselIndex, isCarouselFocused, tileWidth, tileHeight]
  );

  return (
    <WebGLContainer
      label={`carousel-container-${carouselIndex}`}
      key={`carousel-container-${carouselIndex}`}
      y={position.y}
    >
      <>
        {carouselTitle ? (
          <TileTitle
            key={`carousel-title-${carouselIndex}`}
            text={carouselTitle}
            fontSize={22}
            color={"white"}
            position={{ x: padding, y: 10 }}
          />
        ) : null}
        <WebGLCarouselRenderer
          listIndex={carouselIndex}
          key={`carousel-${carouselIndex}`}
          loadingState={CarouselLoadingState.LOADING}
          carouselID={`carousel-${carouselIndex}`}
          carouselIndex={carouselIndex}
          listSize={carouselData?.results.length || 0}
          initialIndex={0}
          easing="power1.out"
          viewportSize={viewportWidth}
          viewportCrossAxisSize={carouselItemHeight}
          enableKeyboardNavigation={isCarouselFocused}
          enableRoamingFocus={false}
          centerFocusedTile={false}
          itemGap={gap}
          itemWidth={tileWidth}
          itemHeight={tileHeight}
          startPadding={padding}
          endPadding={padding}
          renderTile={renderTile}
          overscan={2}
        />
      </>
    </WebGLContainer>
  );
});
